import {
  Component, inject, signal, viewChild, ElementRef, AfterViewInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule, X, Bug, Lightbulb, HelpCircle, Paperclip, Send, AlertCircle
} from 'lucide-angular';
import { FeedbackService } from '../../../core/services/feedback.service';
import { GlobalErrorHandlerService } from '../../../core/services/global-error-handler.service';
import { VersionService } from '../../../core/services/version.service';
import { FeedbackType } from '../../../core/models/feedback.model';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-feedback-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './feedback-modal.component.html',
  styleUrls: ['./feedback-modal.component.scss'],
})
export class FeedbackModalComponent implements AfterViewInit {
  protected readonly feedbackSvc = inject(FeedbackService);
  private readonly errorHandler = inject(GlobalErrorHandlerService);
  private readonly versionSvc = inject(VersionService);

  /** Native <dialog>; opened as a modal on init for built-in backdrop + focus-trap + Esc. */
  private readonly dialog = viewChild<ElementRef<HTMLDialogElement>>('dlg');

  readonly icons = { X, Bug, Lightbulb, HelpCircle, Paperclip, Send, AlertCircle };

  readonly type = signal<FeedbackType>('bug');
  readonly title = signal('');
  readonly description = signal('');
  readonly screenshotFile = signal<File | null>(null);
  readonly screenshotPreviewUrl = signal<string | null>(null);
  readonly submitting = signal(false);
  readonly uploading = signal(false);
  readonly successTicketNumber = signal<string | null>(null);
  readonly errorMsg = signal<string | null>(null);

  // Injected by the opener
  onClose?: () => void;

  ngAfterViewInit(): void {
    const el = this.dialog()?.nativeElement;
    // showModal: top layer, focus trap, Esc closes. Optional chaining - absent in jsdom (no-ops in tests).
    el?.showModal?.();
    // Bound programmatically (not template click) so linters don't flag a mouse-only handler; Esc is the keyboard path, handled natively.
    el?.addEventListener('click', (e) => this.onBackdropClick(e));
  }

  selectType(t: FeedbackType) {
    this.type.set(t);
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      this.errorMsg.set('Screenshot must be under 5 MB.');
      return;
    }
    this.screenshotFile.set(file);
    const reader = new FileReader();
    reader.onload = e => {
      const result = e.target?.result;
      // readAsDataURL always yields a string; guard keeps the type honest.
      if (typeof result === 'string') this.screenshotPreviewUrl.set(result);
    };
    reader.readAsDataURL(file);
  }

  removeScreenshot() {
    this.screenshotFile.set(null);
    this.screenshotPreviewUrl.set(null);
  }

  /** Requests close; routes through the native dialog so Esc and buttons share one path. */
  close() {
    const el = this.dialog()?.nativeElement;
    if (el?.open) el.close?.();
    else this.onClose?.();
  }

  /** Fired by the <dialog> (close) event - Esc, backdrop, or an explicit close(). */
  onClosed(): void {
    this.onClose?.();
  }

  onBackdropClick(e: Event): void {
    // A click whose target is the <dialog> itself lands on the ::backdrop.
    if (e.target === e.currentTarget) {
      this.close();
    }
  }

  async submit() {
    if (!this.title().trim()) {
      this.errorMsg.set('Title is required.');
      return;
    }
    this.errorMsg.set(null);
    this.submitting.set(true);

    let screenshotUrl: string | undefined;
    const file = this.screenshotFile();
    if (file) {
      this.uploading.set(true);
      try {
        const result = await this.feedbackSvc.uploadScreenshot(file).toPromise();
        screenshotUrl = result?.url;
      } catch {
        // non-fatal - submit without screenshot
      }
      this.uploading.set(false);
    }

    this.feedbackSvc.submitTicket({
      type: this.type(),
      title: this.title().trim(),
      description: this.description(),
      screenshot_url: screenshotUrl,
      auto_context: {
        url: globalThis.location.href,
        browser: navigator.userAgent,
        os: navigator.platform,
        app_version: this.versionSvc.frontendVersion,
        user_id: '',
        console_errors: this.errorHandler.consoleErrors(),
      },
    }).subscribe({
      next: ticket => {
        this.successTicketNumber.set(ticket.ticket_number);
        this.submitting.set(false);
      },
      error: (err) => {
        const msg = err?.error?.error ?? 'Failed to submit feedback. Please try again.';
        this.errorMsg.set(msg);
        this.submitting.set(false);
      },
    });
  }
}
